// API client for Cloudflare Pages Functions
// Replaces direct Supabase calls

const API_BASE = '';

interface ApiResponse<T> {
  data?: T;
  error?: string;
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      credentials: 'include', // Include Cloudflare Access cookies
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || `HTTP ${response.status}` };
    }

    return { data };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Network error' };
  }
}

// ============================================
// AUTH / USER
// ============================================

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  role: 'super_admin' | 'leasing_agent' | 'building_manager'; // building_manager is deprecated
  phone?: string | null;
  timezone?: string | null;
}

export interface MeResponse {
  authenticated: boolean;
  authorized: boolean;
  user?: UserProfile;
  buildings?: string[];
  email?: string;
  message?: string;
}

export async function getMe(): Promise<ApiResponse<MeResponse>> {
  return fetchApi('/api/me');
}

export async function updateMyProfile(data: {
  full_name?: string;
  phone?: string;
  timezone?: string;
}): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi('/api/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ============================================
// BUILDINGS
// ============================================

export interface Building {
  id: string;
  name: string;
  address: string;
  city: string;
  postal_code: string | null;
  description: string | null;
  neighborhood_description: string | null;
  amenities: string[];
  latitude: number | null;
  longitude: number | null;
  walk_score: number | null;
  walk_score_description: string | null;
  transit_score: number | null;
  transit_score_description: string | null;
  bike_score: number | null;
  bike_score_description: string | null;
  walk_score_updated_at: string | null;
  leasing_email: string;
  leasing_phone: string | null;
  newcomer_friendly: boolean;
  accepts_proof_of_funds: boolean;
  languages_spoken: string[];
  accessibility: Record<string, unknown>;
  nearby_transit: Array<{ name: string; distance: string; type: string }>;
  nearby_amenities: Array<{ name: string; type: string; distance: string }>;
  current_promotion: { title: string; description?: string; expires_at?: string; terms?: string } | null;
  status_updates: Array<{ id: string; title: string; description: string; category: string; status: string }>;
  slug: string;
  is_active: boolean;
  hero_image_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface UnitType {
  id: string;
  name: string;
  sort_order: number;
}

export interface Unit {
  id: string;
  building_id: string;
  unit_type_id: string | null;
  unit_type: UnitType | null;
  template_id: string | null;
  unit_number: string;
  floor: number;
  price: number;
  square_feet: number | null;
  availability_date: string;
  is_available: boolean;
  features: string[];
  virtual_tour_url: string | null;
  utilities_included: boolean;
  utilities_estimate: number | null;
  parking_cost: number | null;
  key_deposit: number | null;
  accessibility_features: string[];
  promotion_override: string | null;
  created_at: string;
  updated_at: string;
  images?: UnitImage[];
}

export interface UnitImage {
  id: string;
  unit_id: string;
  image_url: string;
  image_type: string;
  caption: string | null;
  sort_order: number;
}

export interface BuildingImage {
  id: string;
  building_id: string;
  image_url: string;
  image_type: string;
  caption: string | null;
  sort_order: number;
}

export interface Testimonial {
  id: string;
  building_id: string;
  tenant_name: string;
  tenant_initial: string | null;
  unit_type: string | null;
  years_resident: string | null;
  quote: string;
  rating: number | null;
  is_featured: boolean;
  is_approved: boolean;
}

export async function getBuildings(): Promise<ApiResponse<{ buildings: Building[] }>> {
  return fetchApi('/api/buildings');
}

export async function getBuilding(slug: string): Promise<ApiResponse<{
  building: Building;
  units: Unit[];
  images: BuildingImage[];
  testimonials: Testimonial[];
}>> {
  return fetchApi(`/api/buildings/${slug}`);
}

export async function updateBuilding(slug: string, data: Partial<Building>): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/buildings/${slug}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function createBuilding(data: Partial<Building>): Promise<ApiResponse<{ id: string; slug: string }>> {
  return fetchApi('/api/buildings', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// UNITS
// ============================================

export async function getUnitTypes(): Promise<ApiResponse<{ unit_types: UnitType[] }>> {
  return fetchApi('/api/unit-types');
}

export async function createUnitType(data: { name: string; sort_order?: number }): Promise<ApiResponse<{ id: string; name: string; sort_order: number }>> {
  return fetchApi('/api/unit-types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateUnitType(id: string, data: { name?: string; sort_order?: number }): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/unit-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteUnitType(id: string): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/unit-types/${id}`, {
    method: 'DELETE',
  });
}

export async function getUnit(id: string): Promise<ApiResponse<{ unit: Unit; building: Building | null }>> {
  return fetchApi(`/api/units/${id}`);
}

export async function updateUnit(id: string, data: Partial<Unit>): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/units/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteUnit(id: string): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/units/${id}`, {
    method: 'DELETE',
  });
}

// ============================================
// WALK SCORE
// ============================================

export interface WalkScoreData {
  walk_score: number | null;
  walk_score_description: string | null;
  transit_score: number | null;
  transit_score_description: string | null;
  bike_score: number | null;
  bike_score_description: string | null;
  updated_at: string | null;
  mode?: 'static' | 'dynamic';
}

export async function getWalkScore(buildingId: string): Promise<ApiResponse<WalkScoreData>> {
  return fetchApi(`/api/walkscore/${buildingId}`);
}

export async function refreshWalkScore(buildingId: string): Promise<ApiResponse<WalkScoreData>> {
  return fetchApi(`/api/walkscore/refresh/${buildingId}`, {
    method: 'POST',
  });
}

// ============================================
// ANALYTICS
// ============================================

export interface AnalyticsMetrics {
  qrScans: number;
  emailClicks: number;
  phoneClicks: number;
  unitViews: number;
  viewingRequests: number;
  shares: number;
}

export interface DailyData {
  date: string;
  qr_scans: number;
  email_clicks: number;
  unit_views: number;
}

export interface AnalyticsResponse {
  metrics: AnalyticsMetrics;
  dailyData: DailyData[];
  range: string;
  totalEvents: number;
}

export async function getAnalytics(
  buildingId: string,
  range: '7d' | '30d' | '90d' = '30d'
): Promise<ApiResponse<AnalyticsResponse>> {
  return fetchApi(`/api/analytics/${buildingId}?range=${range}`);
}

export type AnalyticsEventType =
  | 'qr_scan'
  | 'email_click'
  | 'phone_click'
  | 'unit_view'
  | 'share'
  | 'viewing_request'
  | 'waitlist_signup'
  | 'comparison';

export async function trackEvent(
  eventType: AnalyticsEventType,
  buildingId: string,
  unitId?: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  try {
    await fetchApi('/api/analytics/track', {
      method: 'POST',
      body: JSON.stringify({
        event_type: eventType,
        building_id: buildingId,
        unit_id: unitId,
        metadata,
      }),
    });
  } catch {
    // Analytics should never break UX
    console.error('Analytics tracking failed');
  }
}

// ============================================
// LEASING AGENTS
// ============================================

export interface LeasingAgent {
  id: string;
  building_id: string;
  name: string;
  email: string;
  phone: string | null;
  title: string | null;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export async function getLeasingAgents(buildingId: string): Promise<ApiResponse<{ agents: LeasingAgent[] }>> {
  return fetchApi(`/api/buildings/${buildingId}/agents`);
}

export async function createLeasingAgent(
  buildingId: string,
  data: Omit<LeasingAgent, 'id' | 'building_id' | 'created_at' | 'updated_at'>
): Promise<ApiResponse<{ id: string }>> {
  return fetchApi(`/api/buildings/${buildingId}/agents`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateLeasingAgent(
  buildingId: string,
  agentId: string,
  data: Partial<LeasingAgent>
): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/buildings/${buildingId}/agents/${agentId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteLeasingAgent(
  buildingId: string,
  agentId: string
): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/buildings/${buildingId}/agents/${agentId}`, {
    method: 'DELETE',
  });
}

// ============================================
// WAITLIST
// ============================================

export interface WaitlistEntry {
  building_id: string;
  email: string;
  name?: string;
  phone?: string;
  preferred_unit_types?: string[];
  max_budget?: number;
  move_in_timeline?: string;
}

export async function joinWaitlist(data: WaitlistEntry): Promise<ApiResponse<{ success: boolean; id: string }>> {
  return fetchApi('/api/waitlist', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// USERS
// ============================================

export interface User {
  id: string;
  email: string;
  fullName: string | null;
  role: 'super_admin' | 'leasing_agent';
  assignedBuildings: { id: string; name: string }[];
  createdAt: string;
  updatedAt: string;
}

export async function getUsers(): Promise<ApiResponse<{ users: User[] }>> {
  return fetchApi('/api/users');
}

export async function createUser(data: {
  fullName: string;
  email: string;
  role: string;
  assignedBuildings: string[];
}): Promise<ApiResponse<{ id: string }>> {
  return fetchApi('/api/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateUser(
  userId: string,
  data: {
    fullName?: string;
    email?: string;
    role?: string;
    assignedBuildings?: string[];
  }
): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteUser(userId: string): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/users/${userId}`, {
    method: 'DELETE',
  });
}

// ============================================
// APPLICATIONS
// ============================================

export interface Application {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  preferredMoveIn: string | null;
  applicantType: 'professional' | 'student' | 'newcomer' | 'other';
  hasPets: 'no' | 'dog' | 'cat' | 'other';
  buildingId: string;
  buildingName: string;
  buildingAddress?: string;
  unitId?: string | null;
  unitNumber?: string;
  unitType?: string;
  unitPrice?: number;
  floor?: number;
  status: 'pending' | 'under_review' | 'approved' | 'rejected';
  notes?: string | null;
  submittedAt: string;
  updatedAt: string;
}

export async function getApplications(
  buildingId?: string,
  status?: string
): Promise<ApiResponse<{ applications: Application[] }>> {
  const params = new URLSearchParams();
  if (buildingId) params.set('building_id', buildingId);
  if (status) params.set('status', status);
  const queryString = params.toString();
  return fetchApi(`/api/applications${queryString ? `?${queryString}` : ''}`);
}

export async function getApplication(id: string): Promise<ApiResponse<{ application: Application }>> {
  return fetchApi(`/api/applications/${id}`);
}

export async function updateApplication(
  id: string,
  data: { status?: string; notes?: string }
): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/applications/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ============================================
// HEALTH CHECK
// ============================================

export async function healthCheck(): Promise<ApiResponse<{
  status: string;
  timestamp: string;
  environment: string;
  walkscore_mode: string;
  database: string;
}>> {
  return fetchApi('/api/health');
}

// ============================================
// UNIT TEMPLATES
// ============================================

export interface TemplateImage {
  id: string;
  template_id: string;
  image_url: string;
  image_type: string;
  caption: string | null;
  sort_order: number;
  created_at: string;
}

export interface UnitTemplate {
  id: string;
  building_id: string;
  unit_type_id: string;
  unit_type: { id: string; name: string } | null;
  name: string;
  description: string | null;
  default_square_feet: number | null;
  default_features: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  images: TemplateImage[];
}

export async function getTemplates(buildingId: string): Promise<ApiResponse<{ templates: UnitTemplate[] }>> {
  return fetchApi(`/api/buildings/${buildingId}/templates`);
}

export async function getTemplate(templateId: string): Promise<ApiResponse<{ template: UnitTemplate }>> {
  return fetchApi(`/api/templates/${templateId}`);
}

export async function createTemplate(
  buildingId: string,
  data: {
    unit_type_id: string;
    name: string;
    description?: string;
    default_square_feet?: number;
    default_features?: string[];
    is_active?: boolean;
  }
): Promise<ApiResponse<{ id: string; name: string }>> {
  return fetchApi(`/api/buildings/${buildingId}/templates`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTemplate(
  templateId: string,
  data: Partial<{
    unit_type_id: string;
    name: string;
    description: string | null;
    default_square_feet: number | null;
    default_features: string[];
    is_active: boolean;
  }>
): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/templates/${templateId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteTemplate(templateId: string): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/templates/${templateId}`, {
    method: 'DELETE',
  });
}

// Template Images
export async function addTemplateImages(
  templateId: string,
  images: Array<{ image_url: string; image_type: string; caption?: string; sort_order?: number }>
): Promise<ApiResponse<{ ids: string[] }>> {
  return fetchApi(`/api/templates/${templateId}/images`, {
    method: 'POST',
    body: JSON.stringify(images),
  });
}

export async function removeTemplateImage(
  templateId: string,
  imageId: string
): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/templates/${templateId}/images?imageId=${imageId}`, {
    method: 'DELETE',
  });
}

export async function reorderTemplateImages(
  templateId: string,
  order: Array<{ id: string; sort_order: number }>
): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/templates/${templateId}/images`, {
    method: 'PUT',
    body: JSON.stringify({ order }),
  });
}

// ============================================
// IMAGE LIBRARY
// ============================================

export interface ImageLibraryItem {
  id: string;
  building_id: string;
  image_url: string;
  r2_key: string;
  filename: string;
  file_size: number | null;
  content_type: string | null;
  category: string;
  uploaded_by: string | null;
  created_at: string;
}

export type ImageCategory =
  | 'exterior'
  | 'lobby'
  | 'gym'
  | 'pool'
  | 'laundry'
  | 'parking'
  | 'unit-living'
  | 'unit-kitchen'
  | 'unit-bedroom'
  | 'unit-bathroom'
  | 'unit-balcony'
  | 'floorplan'
  | 'other';

export async function getImageLibrary(
  buildingId: string,
  category?: ImageCategory
): Promise<ApiResponse<{ images: ImageLibraryItem[] }>> {
  const params = category ? `?category=${category}` : '';
  return fetchApi(`/api/buildings/${buildingId}/image-library${params}`);
}

export async function uploadImages(
  buildingId: string,
  files: File[],
  category: ImageCategory
): Promise<ApiResponse<{ uploaded: Array<{ id: string; image_url: string; filename: string }> }>> {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  formData.append('category', category);

  try {
    const response = await fetch(`/api/buildings/${buildingId}/image-library`, {
      method: 'POST',
      credentials: 'include',
      body: formData, // Don't set Content-Type header for FormData
    });

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || `HTTP ${response.status}` };
    }

    return { data };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Network error' };
  }
}

export async function addImageLibraryItems(
  buildingId: string,
  images: Array<{ image_url: string; category: string; filename?: string; r2_key?: string }>
): Promise<ApiResponse<{ uploaded: Array<{ id: string; image_url: string; category: string }> }>> {
  return fetchApi(`/api/buildings/${buildingId}/image-library`, {
    method: 'POST',
    body: JSON.stringify(images),
  });
}

export async function deleteImageLibraryItem(
  buildingId: string,
  imageId: string
): Promise<ApiResponse<{ success: boolean }>> {
  return fetchApi(`/api/buildings/${buildingId}/image-library?id=${imageId}`, {
    method: 'DELETE',
  });
}
